import { useEffect, useState } from 'react';
import { CopyToClipboard } from './CopyToClipboard';
import { useLatestCallbackRef } from '../Hooks/useLatestCallbackRef';
import { SearchIcon } from './icons/SearchIcon';

interface WalletStateProps {
    currency?: string;
    getWalletStatus: () => Promise<{ address: string; balance: number }>;
    getTokenBalance?: () => Promise<number>;
    rootClassname?: string;
    balanceClassname?: string;
    tokenBalanceClassname?: string;
    addressClassname?: string;
    fractionDigits?: number;
    getScreenerUrl?: (adress: string) => string;
}

export function WalletState({
    getWalletStatus,
    getTokenBalance,
    getScreenerUrl,
    currency = '',
    rootClassname = '',
    balanceClassname = '',
    tokenBalanceClassname = '',
    addressClassname = '',
    fractionDigits = 2,
}: WalletStateProps) {
    const [walletBalance, setWalletBalance] = useState<number>(0);
    const [walletAddress, setWalletAddress] = useState<string>('');

    const [tokenBalance, setTokenBalance] = useState<number | undefined>(undefined);

    const getWalletStatusRef = useLatestCallbackRef(getWalletStatus);

    useEffect(() => {
        const updateBalance = async () => {
            try {
                const { address, balance } = await getWalletStatusRef.current();
                setWalletAddress(address);
                setWalletBalance(balance);
            } catch (error) {
                setWalletAddress('');
                setWalletBalance(0);
            }
        };

        updateBalance();

        const intervalId = setInterval(updateBalance, 10000);
        return () => clearInterval(intervalId);
    }, [getWalletStatusRef, walletAddress]);

    const getTokenBalanceRef = useLatestCallbackRef(getTokenBalance ?? (() => Promise.resolve(0)));

    useEffect(() => {
        let isMounted = true;

        const fetchTokenBalance = async () => {
            try {
                const balance = await getTokenBalanceRef.current();
                if (isMounted) setTokenBalance(balance);
            } catch (error) {
                console.error('Error fetching token balance:', error);
                if (isMounted) setTokenBalance(0);
            }
        };

        fetchTokenBalance();
        const intervalId = setInterval(fetchTokenBalance, 5000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [getTokenBalanceRef]);

    return (
        <div className={'flex items-center gap-[10px] ' + rootClassname}>
            <div className={'w-full flex justify-between items-center '}>
                <span className={'font-medium ' + balanceClassname}>
                    {walletBalance.toFixed(fractionDigits)} {currency}
                </span>
                {getTokenBalance && <span className={tokenBalanceClassname}>{`${tokenBalance?.toFixed(fractionDigits)}`}</span>}

                {walletAddress ? (
                    <div className='flex'>
                        <span className={'w-[72px] font-light mr-[7px]' + addressClassname}>{walletAddress.slice(0, 5) + '...' + walletAddress.slice(-5)}</span>
                        <CopyToClipboard className='size-[20px]' copy={walletAddress} />
                    </div>
                ) : null}
            </div>
            {getScreenerUrl && (
                <a href={getScreenerUrl(walletAddress)} target='_blank' rel='noopener noreferrer'>
                    <SearchIcon className='size-[20px]' />
                </a>
            )}
        </div>
    );
}
